import {useState, useEffect, ReactElement, ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {List} from '@tickchak/react-share/app';
import {TShow} from '../../hooks/useShow';
import BackArrow from '../../assets/icons/BackArrow';
import SelectPopupEmpty from './SelectPopupEmpty';

export interface SelectableOption<TId extends string | number = string | number> {
    id: TId;
    text: string;
    subText?: string;
    image?: string | null;
}

interface AddAction {
    text: string;
    emptyText?: string;
    onClick: () => void;
}

export interface SelectPopupEmptyConfig {
    icon?: string;
    text?: ReactNode;
    noResultsText?: (search: string) => ReactNode;
    renderEmpty?: (search: string) => ReactNode;
}

interface SelectPopupProps<TId extends string | number> {
    show: TShow;
    title: string;
    items: SelectableOption<TId>[];
    selectedIds: TId[];
    onConfirm: (ids: TId[]) => void;
    multiSelect?: boolean;
    addAction?: AddAction;
    hideSearch?: boolean;
    empty?: SelectPopupEmptyConfig;
    selectedFirst?: boolean;
    allowDeselect?: boolean;
}

function SelectPopup<TId extends string | number>({
    show, title, items, selectedIds, onConfirm, multiSelect, addAction, hideSearch, empty, selectedFirst, allowDeselect,
}: SelectPopupProps<TId>): ReactElement | null {
    const {t} = useTranslation('timetable');
    const [search, setSearch] = useState('');
    const [draftIds, setDraftIds] = useState<TId[]>(selectedIds);
    const [initialSelectedIds, setInitialSelectedIds] = useState<TId[]>(selectedIds);

    useEffect(() => {
        if(show.isOpen) {
            setDraftIds(selectedIds);
            setInitialSelectedIds(selectedIds);
        } else {
            setSearch('');
        }
    }, [show.isOpen]);

    if(!show.isOpen) return null;

    const searched = search ? items.filter(i => i.text.includes(search)) : items;
    let filtered = searched;
    if(selectedFirst) {
        const initialSet = new Set<TId>(initialSelectedIds);
        const selected = searched.filter(i => initialSet.has(i.id as TId));
        const unselected = searched.filter(i => !initialSet.has(i.id as TId));
        filtered = [...selected, ...unselected];
    }
    const isEmpty = filtered.length === 0;

    const toggleId = (id: TId) => {
        if(multiSelect) {
            setDraftIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        } else if(allowDeselect) {
            setDraftIds(prev => prev.includes(id) ? [] : [id]);
        } else {
            setDraftIds([id]);
        }
    };

    const handleConfirm = () => {
        onConfirm(draftIds);
        show.close();
    };

    return (
        <List
            show={show}
            data-cy="selectPop"
            position={{width: '375px', height: '432px'}}
            ariaLabel={title}
            onItemClick={item => toggleId(item.id as TId)}
        >
            <List.Header
                fixed
                title={title}
                onBack={() => show.close()}
                backIcon={<BackArrow />}
                action={addAction && {variant: 'brand', text: addAction.text, onClick: addAction.onClick}}
            />
            {!hideSearch && <List.Search placeholder={t('search')} onChange={setSearch} autoFocus />}
            <List.Items>
                {isEmpty ? (
                    empty?.renderEmpty
                        ? empty.renderEmpty(search)
                        : <SelectPopupEmpty
                            icon={empty?.icon}
                            text={empty?.text}
                            noResultsText={empty?.noResultsText}
                            search={search}
                            addAction={addAction}
                        />
                ) : (
                    filtered.map(item => (
                        <List.Item.Checkbox
                            key={item.id}
                            id={item.id}
                            text={item.text}
                            subText={item.subText}
                            selected={draftIds.includes(item.id as TId)}
                            avatar={item.image ? {src: item.image, alt: item.text, size: 'lg'} : undefined}
                        />
                    ))
                )}
            </List.Items>
            <List.Footer fixed>
                <List.Footer.Close text={t('close')} size="category"/>
                <List.Footer.Button
                    text={t('confirm')}
                    variant="brand"
                    size="category"
                    onClick={handleConfirm}
                />
            </List.Footer>
        </List>
    );
}

export default SelectPopup;
